import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../core/services/api.service';
import Swal from 'sweetalert2';
import { RoleService } from "../../core/services/role.service";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SocketService } from '../../core/services/socket.service';
import { Subscription } from 'rxjs';
@Component({
  selector: 'app-crew-assignment',
  templateUrl: './crew-assignment.component.html',
  styleUrls: ['./crew-assignment.component.scss'],
  standalone: false
})
export class CrewAssignmentComponent implements OnInit, OnDestroy {
  @ViewChild('shiftChangeModal') shiftChangeModal!: TemplateRef<any>;
  
  incidentId: number | null = null;
  incident: any = null;
  assignedVehicles: any[] = [];
  availableCrew: any[] = [];
  availableDrivers: any[] = [];
  availableVehicles: any[] = []; // For vehicle change
  
  // Crew assignment data
  crewInchargeUserId: number | null = null;
  crewMemberIds: number[] = [];
  driverAssignments: { assignmentId: number, driverUserId: number, vehicleId?: number }[] = [];
  
  loading = false;
  submitting = false;
  error: string | null = null;
  fireStationId: any;
  showVehicleChangeFor: number | null = null; // Track which assignment's vehicle change is shown

  // Properties for shift change (relieve and reassign)
  shiftChangeCrewIncharge: number | null = null;
  shiftChangeCrewMembers: number[] = [];
  shiftChangeDriverAssignments: { assignmentId: number, driverUserId: number }[] = [];
  shiftChangeRemarks: string = '';
  isSubmittingShiftChange = false;
  private socketSubscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    public roleService: RoleService,
    private modalService: NgbModal,
    private socketService: SocketService
  ) {}

  ngOnInit(): void {
    this.incidentId = Number(this.route.snapshot.paramMap.get('id'));
    if (this.incidentId) {
      this.loadIncidentDetails();
      this.loadAssignedVehicles();
      this.loadAvailableVehicles();
      // this.loadAvailableCrew();
      // this.loadAvailableDrivers();

      this.setupSocketListeners();
    } else {
      this.error = 'Invalid incident ID';
    }
  }

  setupSocketListeners(): void {
    // Listen for socket events to reload local data only
    // Global notifications are handled in AppComponent
    const vehicleSub = this.socketService.onVehicleAssigned$.subscribe((data) => {
      if (data && data.incident && data.incident.id === this.incidentId) {
        // Reload assigned vehicles
        this.loadAssignedVehicles();
      }
    });

    const crewSub = this.socketService.onCrewAssigned$.subscribe((data) => {
      if (data && data.incident && data.incident.id === this.incidentId) {
        // Reload incident details
        this.loadIncidentDetails();
      }
    });

    const updateSub = this.socketService.onIncidentUpdated$.subscribe((data) => {
      if (data && data.incident && data.incident.id === this.incidentId) {
        // Reload incident details
        this.loadIncidentDetails();
      }
    });

    this.socketSubscriptions.push(vehicleSub, crewSub, updateSub);
  }

  ngOnDestroy(): void {
    // Unsubscribe from all socket subscriptions
    this.socketSubscriptions.forEach(sub => sub.unsubscribe());
  }

  loadIncidentDetails(): void {
    this.loading = true;
    this.apiService.get(`/incidents/${this.incidentId}`).subscribe({
      next: (response: any) => {
        this.incident = response.data || response;
        this.fireStationId = this.incident.vehicleAssignments[0].fireStationId;
        this.loading = false;

         if (this.incident.crewInchargeUserId) {
          this.crewInchargeUserId = this.incident.crewInchargeUserId;
        }
         this.loadAvailableCrew();
         this.loadAvailableDrivers();
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Failed to load incident details';
        console.error('Error loading incident:', error);
      }
    });
  }

  loadAssignedVehicles(): void {
    this.apiService.get(`/incidents/${this.incidentId}/vehicles`).subscribe({
      next: (response: any) => {
        this.assignedVehicles = response.data || response || [];
        console.log('Assigned vehicles:', this.assignedVehicles);
      },
      error: (error) => {
        console.error('Error loading assigned vehicles:', error);
        this.assignedVehicles = [];
      }
    });
  }

  loadAvailableCrew(): void {
    this.apiService.get(`/fire-station-users/${this.fireStationId}?role=crew_member`).subscribe({
      next: (response: any) => {
        this.availableCrew = response.data || response || [];
        console.log('Available crew:', this.availableCrew);
      },
      error: (error) => {
        console.error('Error loading crew:', error);
        this.availableCrew = [];
      }
    });
  }

  loadAvailableDrivers(): void {
    this.apiService.get(`/fire-station-users/${this.fireStationId}?role=driver`).subscribe({
      next: (response: any) => {
        this.availableDrivers = response.data || response || [];
        console.log('Available drivers:', this.availableDrivers);
      },
      error: (error) => {
        console.error('Error loading drivers:', error);
        this.availableDrivers = [];
      }
    });
  }

  onCrewInchargeChange(userId: number): void {
    this.crewInchargeUserId  = Number(userId) ;
  }

  onCrewMemberToggle(userId: number, event: any): void {
    if (event.target.checked) {
      if (!this.crewMemberIds.includes(userId)) {
        this.crewMemberIds.push(userId);
      }
    } else {
      const index = this.crewMemberIds.indexOf(userId);
      if (index > -1) {
        this.crewMemberIds.splice(index, 1);
      }
    }
  }

  onDriverAssignmentChange(assignmentId: number, driverUserId: number): void {
    const existingAssignment = this.driverAssignments.find(a => a.assignmentId === assignmentId);
    
    if (existingAssignment) {
      existingAssignment.driverUserId = driverUserId;
    } else {
      this.driverAssignments.push({ assignmentId, driverUserId });
    }
  }

  toggleVehicleChange(assignmentId: number): void {
    if (this.showVehicleChangeFor === assignmentId) {
      this.showVehicleChangeFor = null; // Hide if already shown
    } else {
      this.showVehicleChangeFor = assignmentId; // Show for this assignment
    }
  }

  onVehicleChange(assignmentId: number, newVehicleId: number): void {
    const existingAssignment = this.driverAssignments.find(a => a.assignmentId === assignmentId);
    
    if (existingAssignment) {
      existingAssignment.vehicleId = newVehicleId;
    } else {
      this.driverAssignments.push({ assignmentId, driverUserId: 0, vehicleId: newVehicleId });
    }
  }

  loadAvailableVehicles(): void {
    this.apiService.get('/fims-vehicles?status=available').subscribe({
      next: (response: any) => {
        this.availableVehicles = response.data || response || [];
        console.log('Available vehicles:', this.availableVehicles);
      },
      error: (error) => {
        console.error('Error loading available vehicles:', error);
        this.availableVehicles = [];
      }
    });
  }

  submitCrewAssignment(): void {
    if (!this.crewInchargeUserId ) {
      Swal.fire({
        icon: 'warning',
        title: 'Crew Incharge Required',
        text: 'Please select a crew incharge before assigning.',
        confirmButtonColor: '#B71C1C'
      });
      return;
    }

    const vehiclesNeedingDriver = this.assignedVehicles.filter((v: any) => !v.driver);

    if (this.driverAssignments.length !== vehiclesNeedingDriver.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Driver Assignment Required',
        text: 'Please assign a driver to each vehicle.',
        confirmButtonColor: '#B71C1C'
      });
      return;
    }

    this.submitting = true;

    const assignmentData = {
      crewInchargeUserId: this.crewInchargeUserId,
      crewMemberIds: this.crewMemberIds,
      driverAssignments: this.driverAssignments
    };

    this.apiService.post(`/incidents/${this.incidentId}/assign-crew`, assignmentData).subscribe({
      next: (response: any) => {
        this.submitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Crew Assigned Successfully!',
          text: `Crew has been assigned to incident ${this.incident?.incidentNo}.`,
          confirmButtonColor: '#B71C1C'
        }).then(() => {
          this.router.navigate(['/incidents']);
        });
      },
      error: (error) => {
        this.submitting = false;
        console.error('Error assigning crew:', error);
        Swal.fire({
          icon: 'error',
          title: 'Assignment Failed',
          text: 'Failed to assign crew. Please try again.',
          confirmButtonColor: '#B71C1C'
        });
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/incidents']);
  }

  // Shift change methods
  openShiftChangeModal(content: any): void {
    this.modalService.open(content, { size: 'xl', centered: true, scrollable: true });
  }

  onShiftCrewInchargeChange(crewId: string): void {
    this.shiftChangeCrewIncharge = crewId ? parseInt(crewId) : null;
  }

  onShiftCrewMemberToggle(crewId: number, event: any): void {
    if (event.target.checked) {
      if (!this.shiftChangeCrewMembers.includes(crewId)) {
        this.shiftChangeCrewMembers.push(crewId);
      }
    } else {
      const index = this.shiftChangeCrewMembers.indexOf(crewId);
      if (index > -1) {
        this.shiftChangeCrewMembers.splice(index, 1);
      }
    }
  }

  onShiftDriverAssignmentChange(assignmentId: number, driverUserId: string): void {
    if (!driverUserId) return;

    const driverId = parseInt(driverUserId);
    const existingIndex = this.shiftChangeDriverAssignments.findIndex(d => d.assignmentId === assignmentId);

    if (existingIndex > -1) {
      this.shiftChangeDriverAssignments[existingIndex].driverUserId = driverId;
    } else {
      this.shiftChangeDriverAssignments.push({
        assignmentId: assignmentId,
        driverUserId: driverId
      });
    }
  }

  submitShiftChange(): void {
    if (!this.incidentId) return;

    // Build driver assignments array with vehicleId
    const driverAssignments = this.shiftChangeDriverAssignments.map(assignment => {
      const vehicleAssignment = this.assignedVehicles.find(a => a.id === assignment.assignmentId);
      return {
        assignmentId: assignment.assignmentId,
        driverUserId: assignment.driverUserId,
        vehicleId: vehicleAssignment?.vehicleId
      };
    });

    const requestData: any = {
      remarks: this.shiftChangeRemarks || 'Shift change'
    };

    if (this.shiftChangeCrewIncharge) {
      requestData.crewInchargeUserId = this.shiftChangeCrewIncharge;
    }

    if (this.shiftChangeCrewMembers.length > 0) {
      requestData.crewMemberIds = this.shiftChangeCrewMembers;
    }

    if (driverAssignments.length > 0) {
      requestData.driverAssignments = driverAssignments;
    }

    // Validate that at least one field is being updated
    if (!requestData.crewInchargeUserId && !requestData.crewMemberIds && !requestData.driverAssignments) {
      Swal.fire({
        icon: 'warning',
        title: 'No Changes',
        text: 'Please select at least one crew member, crew incharge, or driver to reassign.',
        confirmButtonColor: '#B71C1C'
      });
      return;
    }

    this.isSubmittingShiftChange = true;

    this.apiService.post(`/incidents/${this.incidentId}/relieve-and-reassign`, requestData).subscribe({
      next: (response: any) => {
        // Close modal
        this.modalService.dismissAll();

        Swal.fire({
          icon: 'success',
          title: 'Shift Change Completed',
          text: 'Crew shift change has been completed successfully.',
          confirmButtonColor: '#B71C1C'
        });

        // Reset form
        this.shiftChangeCrewIncharge = null;
        this.shiftChangeCrewMembers = [];
        this.shiftChangeDriverAssignments = [];
        this.shiftChangeRemarks = '';

        // Reload incident data
        this.loadIncidentDetails();
        this.loadAssignedVehicles();
      },
      error: (error) => {
        console.error('Error submitting shift change:', error);
                this.isSubmittingShiftChange = false;

        Swal.fire({
          icon: 'error',
          title: 'Shift Change Failed',
          text: error.error?.message || 'Failed to complete shift change. Please try again.',
          confirmButtonColor: '#B71C1C'
        });
      },
      complete: () => {
        this.isSubmittingShiftChange = false;
      }
    });
  }
}
